/**
 * GenerateAspectsDialog — single-topic AI aspect generator.
 *
 * Triggered by the "✨ Generate aspects with AI" button inside
 * PostBreakdownEditor. Calls `POST /api/agent/content-plan/topics/:topicId/generate-aspects`
 * via `ContentPlanApiClient.generateAspects()` and closes — backend persists
 * the merged `{ plannedPostCount, postBreakdown }` on the topic doc, and the
 * Firestore subscription on `topic_backlog/{topicId}` propagates the change
 * to the editor automatically.
 *
 * Two modes (radio):
 *   - APPEND (default) — adds `count` NEW aspects to existing breakdown.
 *     Existing aspects are surfaced as a read-only preview list AND injected
 *     into the AI prompt under "## Existing aspects (do NOT duplicate)" so
 *     the AI generates non-overlapping angles. Final count slider label
 *     reads "Add N aspects" and result row is "M existing + N new = M+N total".
 *   - REPLACE — wipes existing aspects, writes only the freshly-generated `count`.
 *     A red warning Alert appears when M existing aspects are present.
 *
 * Per product policy, this single-topic dialog has the freeform
 * `userInstruction` field; the bulk-aspects backlog action does not.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Box, Typography, TextField, Slider, Switch,
  FormControlLabel, FormControl, InputLabel, Select, MenuItem,
  Chip, Alert, RadioGroup, Radio, Paper,
} from '@mui/material';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { useAuthor } from '../../hooks/useAuthor';
import { usePillars } from '../../hooks/usePillars';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import type { ContentTopic } from '../../types';

const TESTIDS = {
  dialog:           'aspects-generate-dialog',
  count:            'aspects-generate-count',
  model:            'aspects-generate-model',
  instruction:      'aspects-generate-instruction',
  authorToggle:     'aspects-generate-author-toggle',
  pillars:          'aspects-generate-pillars',
  modeAppend:       'aspects-generate-mode-append',
  modeReplace:      'aspects-generate-mode-replace',
  existingPreview:  'aspects-generate-existing-preview',
  submit:           'aspects-generate-submit',
  cancel:           'aspects-generate-cancel',
} as const;

interface ExistingAspect {
  aspectKey: string;
  aspectLabel: string;
  aspectAngle?: string;
}

export interface GenerateAspectsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string;
  topic: ContentTopic;
  onSuccess?: (taskId: string) => void;
}

export const GenerateAspectsDialog: React.FC<GenerateAspectsDialogProps> = ({
  open, onClose, projectId, userId, authorId, topic, onSuccess,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const { author } = useAuthor(projectId);
  const { pillars: allPillars } = usePillars(projectId);
  const confirmedPillars = useMemo(
    () => allPillars.filter((p) => !p.isDraft),
    [allPillars],
  );

  const existingAspects: ExistingAspect[] = useMemo(() => {
    const arr = (topic as any).postBreakdown;
    return Array.isArray(arr) ? arr : [];
  }, [topic]);

  const [count, setCount] = useState<number>(3);
  const [mode, setMode] = useState<'append' | 'replace'>('append');
  const [model, setModel] = useState<string>(projectDefaultModel.value);
  const [userInstruction, setUserInstruction] = useState<string>('');
  const [includeAuthor, setIncludeAuthor] = useState<boolean>(true);
  const [selectedPillarIds, setSelectedPillarIds] = useState<string[]>(() => {
    const fromTopic = (topic as any).pillarIds as string[] | undefined;
    return Array.isArray(fromTopic) ? fromTopic : [];
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Re-prefill selection from topic when the dialog re-opens for a different topic.
  // Mode defaults: append when existing aspects are present (the common case),
  // replace makes no sense as a default — opt-in only.
  useEffect(() => {
    if (!open) return;
    setCount(3);
    setMode('append');
    setModel(projectDefaultModel.value);
    setUserInstruction('');
    setError(null);
    const fromTopic = (topic as any).pillarIds as string[] | undefined;
    setSelectedPillarIds(Array.isArray(fromTopic) ? fromTopic : []);
    // includeAuthor + model deliberately kept across opens (user preference).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topic.id, projectDefaultModel.value]);

  const authorAvailable = !!author && (
    !!author.positioning ||
    !!author.expertise ||
    !!author.uniqueValueProposition ||
    !!author.originStory ||
    (Array.isArray(author.values) && author.values.length > 0)
  );

  const hasExisting = existingAspects.length > 0;
  const finalTotal = mode === 'append'
    ? existingAspects.length + count
    : count;

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const client = new ContentPlanApiClient();
      const resp = await client.generateAspects({
        projectId,
        userId,
        authorId,
        topicId: topic.id,
        count,
        mode,
        model,
        userInstruction: userInstruction.trim() || undefined,
        includeAuthor,
        selectedPillarIds,
      });
      if (!resp.success) throw new Error(resp.error ?? 'Aspect generation failed');
      onSuccess?.(resp.taskId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="sm" data-testid={TESTIDS.dialog}>
      <DialogHeader title="✨ Generate aspects with AI" onClose={onClose} />
      <DialogContent>
        <Stack spacing={2.5} sx={{ py: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {/* Existing aspects preview — shown only when topic already has aspects. */}
          {hasExisting && (
            <Paper
              variant="outlined"
              sx={{ p: 1.25, bgcolor: 'action.hover' }}
              data-testid={TESTIDS.existingPreview}
            >
              <Typography variant="caption" color="text.secondary">
                EXISTING ASPECTS ({existingAspects.length})
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {existingAspects.map((a, i) => (
                  <Typography key={i} variant="body2">
                    {i + 1}. <strong>{a.aspectLabel}</strong>
                    {a.aspectAngle && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        — {a.aspectAngle}
                      </Typography>
                    )}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Mode selector — only relevant when existing aspects are present. */}
          {hasExisting && (
            <FormControl>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                MODE
              </Typography>
              <RadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value as 'append' | 'replace')}
              >
                <FormControlLabel
                  value="append"
                  control={<Radio size="small" inputProps={{ 'data-testid': TESTIDS.modeAppend } as any} />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        Add to existing
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate new aspects that complement the {existingAspects.length} existing — AI is told not to duplicate them.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="replace"
                  control={<Radio size="small" inputProps={{ 'data-testid': TESTIDS.modeReplace } as any} />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        Replace all
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Wipe the {existingAspects.length} existing aspect{existingAspects.length === 1 ? '' : 's'} and start over.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
              {mode === 'replace' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This will permanently delete {existingAspects.length} existing aspect{existingAspects.length === 1 ? '' : 's'}.
                  Already-generated master posts referenced by their <code>aspectKey</code> will become orphaned.
                </Alert>
              )}
            </FormControl>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {mode === 'append' && hasExisting
                ? `Add ${count} aspect${count === 1 ? '' : 's'} → total ${finalTotal}`
                : `Number of aspects: ${count}`}
            </Typography>
            <Slider
              value={count}
              onChange={(_, v) => setCount(v as number)}
              min={2} max={8} step={1} marks
              valueLabelDisplay="auto"
              data-testid={TESTIDS.count}
              sx={{ maxWidth: 360 }}
            />
            <Typography variant="caption" color="text.secondary">
              Each aspect becomes a separate master post.
            </Typography>
          </Box>

          <AvailableModelSelector
            projectId={projectId}
            value={model}
            onChange={setModel}
            label="Model"
            sx={{ width: '100%' }}
            testId={TESTIDS.model}
          />

          <TextField
            label="Direction (optional)"
            multiline
            minRows={2}
            maxRows={6}
            value={userInstruction}
            onChange={(e) => setUserInstruction(e.target.value)}
            placeholder='e.g. "Bipolar arc: pain -> fix" or "Sequence the aspects context -> method -> proof"'
            inputProps={{ 'data-testid': TESTIDS.instruction }}
            helperText="When set, this is the primary direction. Leave empty to let the model pick."
          />

          {authorAvailable && (
            <FormControlLabel
              control={
                <Switch
                  checked={includeAuthor}
                  onChange={(e) => setIncludeAuthor(e.target.checked)}
                  size="small"
                  inputProps={{ 'data-testid': TESTIDS.authorToggle } as any}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Include author context</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Positioning, expertise, UVP, values, origin story — used as the voice lens.
                  </Typography>
                </Box>
              }
            />
          )}

          {confirmedPillars.length > 0 && (
            <Box>
              <FormControl fullWidth size="small" data-testid={TESTIDS.pillars}>
                <InputLabel>
                  Pillars ({selectedPillarIds.length}/{confirmedPillars.length})
                </InputLabel>
                <Select
                  multiple
                  value={selectedPillarIds}
                  label={`Pillars (${selectedPillarIds.length}/${confirmedPillars.length})`}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedPillarIds(typeof v === 'string' ? v.split(',') : (v as string[]));
                  }}
                  renderValue={(vals) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(vals as string[]).map((id) => {
                        const p = confirmedPillars.find((pp) => pp.id === id);
                        return <Chip key={id} size="small" label={p?.name ?? id} />;
                      })}
                    </Box>
                  )}
                >
                  {confirmedPillars.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{p.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Pillars define the thematic territory. Empty selection = topic angle only.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel', onClick: onClose, 'data-testid': TESTIDS.cancel,
        } as any}
        primaryAction={{
          label: submitting
            ? 'Generating…'
            : mode === 'append' && hasExisting
              ? `Add ${count} aspect${count === 1 ? '' : 's'}`
              : 'Generate aspects',
          onClick: handleSubmit,
          disabled: submitting,
          color: mode === 'replace' && hasExisting ? 'error' : 'primary',
          'data-testid': TESTIDS.submit,
        } as any}
      />
    </BaseDialog>
  );
};

export default GenerateAspectsDialog;
